import { MoreVert } from '@mui/icons-material';
import { Box, IconButton, Link, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import DownloadPopup from '/@/components/bayer/download-popup';
import ChipLabel from '/@/components/chip-label';
import { ShareItemPopup } from '/@/components/share-item-popup';
import { useGetUserNode } from '/@/hooks/nodes';
import { useHasWriteAccess } from '/@/hooks/session';
import { useToggle } from '/@/hooks/toggle/useToggle';
import type { FieldObservation, VisitReportList } from '/@/hooks/visit-reports';
import { useDeleteVisitReport } from '/@/hooks/visit-reports';
import { _ } from '/@/locale';
import { getSessionLanguage } from '/@/store/session/selectors';
import type { VisitReport } from '/@/types/field';
import { formatDate } from '/@/utils';
import { reverse } from '/@/utils/router';
import { generateVisitReportPDF } from '/@/views/visit-reports/result/hooks';
import { separateBlocks } from '/@/views/visit-reports/rich-text';
import { getUserDisplayName, RegistryFooter } from './card-footer';
import { Cell, COLUMNS } from './data-grid';
import { RegistryCard } from './registry-card';
import { TablaCard, TablaCardRow } from './tabla-card';

/** La observación general ahora se guarda como HTML (editor); para la vista previa mostramos solo el texto. */
const toPlainText = (value?: string | null) => {
    if (!value) return '';
    const doc = new DOMParser().parseFromString(separateBlocks(value), 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

const RegistryVisit = ({
    visitReport,
    fieldObservation,
    expanded,
    onToggle,
}: {
    visitReport: VisitReportList;
    fieldObservation?: FieldObservation;
    /** Mobile-only collapse state, injected by `Registries`' accordion. */
    expanded?: boolean;
    onToggle?: () => void;
}) => {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const navigate = useNavigate();
    const hasWriteAccess = useHasWriteAccess();
    const deleteVisitReport = useDeleteVisitReport({ visitReportId: visitReport.id });
    const userNodeQuery = useGetUserNode(visitReport.createdBy ?? 0);
    const language = useSelector(getSessionLanguage);
    const { open: openSharePopup, setOpen: setOpenSharePopup, setClose: setCloseSharePopup } = useToggle();
    const { open: openDownloadPopup, setOpen: setOpenDownloadPopup, setClose: setCloseDownloadPopup } = useToggle();

    const closeMenu = () => setMenuAnchor(null);

    const authorName = getUserDisplayName(userNodeQuery.data);
    const registeredAt = formatDate(new Date(visitReport.created));

    const handleDelete = async () => {
        closeMenu();
        await deleteVisitReport.trigger();
    };

    return (
        <RegistryCard
            header={
                <Typography variant='h6' color='primary'>
                    {/* A remote report was made without visiting the field */}
                    {visitReport.type === 'remote'
                        ? _('Informe a distancia')
                        : onToggle
                          ? _('Visita')
                          : _('Visita al lote')}
                </Typography>
            }
            menu={
                // Mobile only surfaces "Ver informe" as a visible link in the body (see
                // below) — Editar informe/Descargar/Compartir/Eliminar have no mock there,
                // so they're dropped rather than guessed at.
                onToggle ? undefined : (
                    <>
                        <IconButton
                            size='small'
                            aria-label={_('Más opciones')}
                            onClick={(event) => setMenuAnchor(event.currentTarget)}
                        >
                            <MoreVert fontSize='small' />
                        </IconButton>
                        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                            {visitReport.status === 'saved' && (
                                <MenuItem
                                    onClick={() => {
                                        closeMenu();
                                        navigate(
                                            reverse('visitReport.VisitReportDetail', {
                                                params: { visitReportId: visitReport.id },
                                            })
                                        );
                                    }}
                                >
                                    {_('Ver informe')}
                                </MenuItem>
                            )}
                            {hasWriteAccess && (
                                <MenuItem
                                    onClick={() => {
                                        closeMenu();
                                        navigate(
                                            reverse('visitReport.VisitReportEdit', {
                                                params: { visitReportId: visitReport.id },
                                            })
                                        );
                                    }}
                                >
                                    {_('Editar informe')}
                                </MenuItem>
                            )}
                            <MenuItem
                                onClick={() => {
                                    closeMenu();
                                    setOpenDownloadPopup();
                                }}
                            >
                                {_('Descargar')}
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    closeMenu();
                                    setOpenSharePopup();
                                }}
                            >
                                {_('Compartir')}
                            </MenuItem>
                            {hasWriteAccess && (
                                <MenuItem onClick={handleDelete} disabled={deleteVisitReport.isMutating}>
                                    {_('Eliminar')}
                                </MenuItem>
                            )}
                        </Menu>

                        <ShareItemPopup
                            visible={openSharePopup}
                            onClose={setCloseSharePopup}
                            itemType='visit-report'
                            item={visitReport as unknown as VisitReport}
                            entrySource='result'
                        />

                        <DownloadPopup
                            visible={openDownloadPopup}
                            title={_('Descargá tu informe de visita')}
                            onClose={setCloseDownloadPopup}
                            onDownloadBefore={async (file) => {
                                if (file) return file;
                                return generateVisitReportPDF({ visitReportId: visitReport.id, language });
                            }}
                            onDownloadAfter={() => setCloseDownloadPopup()}
                            beforeDownload={_(
                                'Estamos generando el PDF. Una vez listo, podrás descargarlo en unos segundos.'
                            )}
                            afterDownload={_(
                                '¡Tu informe de visita ya está listo!\nUna vez descargado, lo vas a encontrar en tu carpeta de Descargas.'
                            )}
                        />
                    </>
                )
            }
            footer={<RegistryFooter authorName={authorName} date={registeredAt} />}
            expanded={expanded}
            onToggle={onToggle}
        >
            {onToggle ? (
                <TablaCard>
                    <TablaCardRow label={_('Fecha')} value={formatDate(new Date(visitReport.date))} />
                    <TablaCardRow
                        label={_('Fenología en la visita')}
                        value={
                            fieldObservation?.vegetativeStage ? (
                                <ChipLabel label={fieldObservation.vegetativeStage} type='fallow' />
                            ) : (
                                '—'
                            )
                        }
                    />
                    <Stack spacing={0.5} sx={{ width: '100%' }}>
                        <Typography variant='body2' color='primary'>
                            {_('Observación general')}
                        </Typography>
                        <Typography variant='caption' color='primary'>
                            {toPlainText(visitReport.observation) || '—'}
                        </Typography>
                    </Stack>
                    {visitReport.status === 'saved' && (
                        <Link
                            component='button'
                            sx={{ textAlign: 'left' }}
                            underline='always'
                            variant='body2'
                            color='secondary'
                            onClick={() =>
                                navigate(
                                    reverse('visitReport.VisitReportDetail', {
                                        params: { visitReportId: visitReport.id },
                                    })
                                )
                            }
                        >
                            {_('Ver informe')}
                        </Link>
                    )}
                </TablaCard>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: COLUMNS, gap: 1, overflow: 'auto', width: '100%' }}>
                    <Cell header>{_('Fecha')}</Cell>
                    <Cell header>{_('Fenología en la visita')}</Cell>
                    <Cell header>{_('Observación general')}</Cell>
                    <Cell>{formatDate(new Date(visitReport.date))}</Cell>
                    <Cell>
                        {fieldObservation?.vegetativeStage ? (
                            <ChipLabel label={fieldObservation.vegetativeStage} type='fallow' />
                        ) : (
                            '—'
                        )}
                    </Cell>
                    <Cell>
                        {(() => {
                            const observation = toPlainText(visitReport.observation);
                            if (!observation) return '—';
                            return observation.length > 150 ? `${observation.slice(0, 150)}...` : observation;
                        })()}
                    </Cell>
                </Box>
            )}
        </RegistryCard>
    );
};

export default RegistryVisit;
